import { useCallback } from 'react';

interface UseMouseHandlersProps {
  panning: {
    handleMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    handleMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    handleMouseUp: () => void;
    handleMouseLeave: () => void;
  };
  interaction: {
    handleMouseUp: (e: React.MouseEvent<HTMLCanvasElement>) => void;
    handleDoubleClick: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  };
}

interface UseMouseHandlersReturn {
  handleMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseUp: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseLeave: () => void;
  handleDoubleClick: (e: React.MouseEvent<HTMLCanvasElement>) => void;
}

/**
 * Hook for combining panning and interaction mouse handlers
 * Coordinates between panning (drag) and click interactions
 */
export function useMouseHandlers({ panning, interaction }: UseMouseHandlersProps): UseMouseHandlersReturn {
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    panning.handleMouseDown(e);
  }, [panning]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    panning.handleMouseMove(e);
  }, [panning]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    interaction.handleMouseUp(e); // Handles clicks if not panned
    panning.handleMouseUp(); // Finalizes panning state
  }, [interaction, panning]);

  const handleMouseLeave = useCallback(() => {
    panning.handleMouseLeave();
  }, [panning]);

  const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    interaction.handleDoubleClick(e);
  }, [interaction]);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleDoubleClick
  };
}
